/**
 * `AxisLabels` — the two axis titles every axis-bearing chart draws (launch round,
 * 2026-10-07: "clean and neatly aligned, labelled x and y axes").
 *
 * The x title is centred under the plot, in the band below the tick row; the y title is
 * rotated −90° and centred on the plot's height, in the band left of the label column. Both
 * are 11 px `--ink-2`. The bands' sizes come from `scale.ts`, so every chart reserves the
 * same room and the titles line up from card to card. A title that is empty draws nothing.
 *
 * A title longer than its axis is shortened with an ellipsis rather than run off the frame
 * (an SVG clips at its viewBox, silently); the full words are on its `<title>`.
 */
import { AXIS_LABEL_INK, AXIS_LABEL_SIZE, AXIS_Y_BAND, fitAxisTitle } from './scale';

export interface AxisLabelsProps {
  xLabel?: string;
  yLabel?: string;
  /** The plot's extent in the chart's own (outer) coordinates. */
  plotLeft: number;
  plotRight: number;
  plotTop: number;
  plotBottom: number;
  /** The chart's full height, the x-title band included. */
  height: number;
}

export function AxisLabels({ xLabel, yLabel, plotLeft, plotRight, plotTop, plotBottom, height }: AxisLabelsProps) {
  // Each title is centred on its plot and may overhang it a little at both ends.
  const xText = xLabel ? fitAxisTitle(xLabel, Math.max(plotRight - plotLeft, 0) + 20) : '';
  const yText = yLabel ? fitAxisTitle(yLabel, Math.max(plotBottom - plotTop + 2 * Math.min(plotTop, 8), height - 4)) : '';
  return (
    <>
      {xLabel ? (
        <text
          className="chart-axis-title"
          data-axis-label="x"
          x={(plotLeft + plotRight) / 2}
          y={height - 4}
          textAnchor="middle"
          fontSize={AXIS_LABEL_SIZE}
          fill={AXIS_LABEL_INK}
        >
          {xText !== xLabel ? <title>{xLabel}</title> : null}
          {xText}
        </text>
      ) : null}
      {yLabel ? (
        <text
          className="chart-axis-title"
          data-axis-label="y"
          transform={`translate(${AXIS_Y_BAND - 4} ${(plotTop + plotBottom) / 2}) rotate(-90)`}
          textAnchor="middle"
          fontSize={AXIS_LABEL_SIZE}
          fill={AXIS_LABEL_INK}
        >
          {yText !== yLabel ? <title>{yLabel}</title> : null}
          {yText}
        </text>
      ) : null}
    </>
  );
}

export default AxisLabels;
